import { QdsActionButtonsProps } from "./QdsSignDetail.types";
import { Button } from "../../components/button";
import { Icon } from "../../modules/icon";
import { useTranslation } from "react-i18next";

/**
 * Determines if user should see "Potpisi dokument" (Sign Document) button
 * 
 * Now uses priority-based signing order:
 * 1. First, all counter party signatures must sign in priority order (prio 1, 2, 3...)
 * 2. Then, all holder signatures sign in priority order
 * Only visible if it's the current user's turn to sign
 */
const shouldShowSignButton = (qdsRequest: any, currentUserId: number | undefined, signingOrder: any): boolean => {
  if (!qdsRequest || !currentUserId || !signingOrder) return false;

  // Only show if it's the user's turn and they can sign
  return signingOrder.isUserTurn && signingOrder.canSign;
};

/**
 * Determines if user should see "Daj saglasnost" (Give Consent) button
 * Visible if user is in consents list AND their status is "pending"
 * Consent is not affected by priority order - can be approved anytime
 */
const shouldShowConsentButton = (qdsRequest: any, currentUserId: number | undefined): boolean => {
  if (!qdsRequest || !currentUserId) return false;

  // Check if user is in consents and their status is pending
  const userConsent = qdsRequest.consents?.find(
    (consent: any) => consent.userId === currentUserId
  );

  return !!userConsent && userConsent.status === "pending";
};

export const QdsActionButtons = ({
  qdsRequest,
  currentUser,
  onSignDocument,
  onGiveConsent,
  signingDocument,
  givingConsent,
  signingOrder,
}: QdsActionButtonsProps) => {
  const { t } = useTranslation();
  const currentUserId = currentUser?.backEndId ? Number(currentUser.backEndId) : undefined;

  const showSignButton = shouldShowSignButton(qdsRequest, currentUserId, signingOrder);
  const showConsentButton = shouldShowConsentButton(qdsRequest, currentUserId);

  // If no buttons should be shown, return null
  if (!showSignButton && !showConsentButton) {
    return null;
  }

  return (
    <div className="d-flex flex-row gap-2 flex-wrap">
      {showConsentButton && (
        <Button
          className="btn btn-secondary"
          onClick={onGiveConsent}
          disabled={givingConsent}
        >
          <Icon type="fa-solid fa-check" size={16} />
          &nbsp; {t("DOCUMENTS.GIVE_CONSENT")}
        </Button>
      )}
      {showSignButton && (
        <Button
          className="btn btn-primary"
          onClick={onSignDocument}
          disabled={signingDocument}
        >
          <Icon type="fa-solid fa-pen-nib" size={16} />
          &nbsp; {t("DOCUMENTS.SIGN_DOCUMENT")}
        </Button>
      )}
    </div>
  );
};
